import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import moment from 'moment';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Dropdown} from 'primereact/dropdown';
import {AutoComplete} from 'primereact/autocomplete';
import {InputText} from 'primereact/inputtext';
import {InputNumber} from 'primereact/inputnumber';
import {Checkbox} from 'primereact/checkbox';
import {Message} from 'primereact/message';
import {Tag} from 'primereact/tag';
import {Skeleton} from 'primereact/skeleton';
import ProductionDataTable from '../productionstatusfloorplan/ProductionDataTable';

const api = (method, path, data, params, signal) => axios({method, url: `${process.env.REACT_APP_API_HOST}/api/v2/machine-capacity${path}`,
  data, params, signal, headers: {Authorization: `Bearer ${localStorage.getItem('userToken')}`, 'App-Network': localStorage.getItem('networkId')}});

export default function MachineCapacityComponent() {
  const {t, i18n} = useTranslation('common');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(() => ({from: moment().format('YYYY-MM-DD'), until: '', include_done: false}));
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const searchSequence = useRef(0);
  const canAssign = (localStorage.getItem('roles') || '[]').includes('role_project_write');
  const network = localStorage.getItem('networkId');
  const explainError = e => {
    const body = e?.response?.data;
    return body?.detail || body?.message || t('error');
  };
  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;
    let timer;
    setLoading(true);
    const load = async () => {
      try {
        const response = await api('get', '', null, {...filters, include_done: filters.include_done ? 1 : 0}, controller.signal);
        if (!stopped) { setMachines(response.data.data.items); setError(''); }
      } catch (e) {
        if (!stopped) setError(e?.response?.data?.detail || e?.response?.data?.message || 'capacityLoadFailed');
      } finally {
        if (!stopped) { setLoading(false); timer = setTimeout(load, 10000); }
      }
    };
    load();
    return () => { stopped = true; controller.abort(); clearTimeout(timer); };
  }, [filters, refresh, network]);
  const search = async ({query}) => {
    const sequence = ++searchSequence.current;
    try {
      const response = await api('get', '/operations', null, {q: query});
      if (sequence === searchSequence.current) setSuggestions(response.data.data.items);
    } catch (e) { if (sequence === searchSequence.current) setDialogError(explainError(e)); }
  };
  const open = machine => {
    setSelected(machine); setDialogError(''); setSuggestions([]);
    setForm({date: moment().format('YYYY-MM-DD'), group: machine.groups.length === 1 ? machine.groups[0].id : null, operation: null, quantity: 1, minutes: null, note: ''});
  };
  const save = async () => {
    setSaving(true); setDialogError('');
    try {
      await api('post', '/assign', {machine_id: selected.id, schedule_group_id: form.group, production_item_id: form.operation.id,
        date: form.date, quantity: form.quantity, standard_minutes: form.minutes, note: form.note});
      setSelected(null); setRefresh(value => value + 1);
      window.App.toastShow('success', t('capacitySaved'));
    } catch (e) {
      const errors = e?.response?.data?.data;
      setDialogError(errors && typeof errors === 'object' ? Object.values(errors).flat().filter(v => typeof v === 'string').join(' · ') || explainError(e) : explainError(e));
    } finally { setSaving(false); }
  };
  const number = value => new Intl.NumberFormat(i18n.language, {maximumFractionDigits: 2}).format(value);
  const group = selected?.groups.find(g => g.id === form.group);
  return <>
    <div className="card p-mb-3 p-d-flex p-ai-end p-flex-wrap" style={{gap: '1rem'}}>
      {['from', 'until'].map(field => <div className="p-field p-mb-0" key={field}><label htmlFor={`capacity-${field}`} className="p-d-block">{t(field === 'from' ? 'capacityFrom' : 'capacityUntil')}</label>
        <InputText id={`capacity-${field}`} type="date" value={filters[field]} onChange={e => setFilters({...filters, [field]: e.target.value})}/></div>)}
      <div><Checkbox inputId="capacity-done" checked={filters.include_done} onChange={e => setFilters({...filters, include_done: e.checked})}/><label htmlFor="capacity-done" className="p-ml-2">{t('capacityShowDone')}</label></div>
      <Button icon="pi pi-refresh" label={t('refresh')} onClick={() => setRefresh(value => value + 1)}/>
    </div>
    {error && <Message severity="error" text={t(error, {defaultValue: error})}/>}
    {loading && machines.length === 0 && <Skeleton height="10rem"/>}
    {!loading && !error && machines.length === 0 && <Message severity="info" text={t('capacityNoMachines')}/>}
    {machines.map(machine => <section key={machine.id} className="card p-mb-3">
      <h3>{machine.name}</h3>
      <ProductionDataTable items={machine.items} showSchedule renderStatus={status => <Tag value={t(`capacityStatus.${status}`, {defaultValue: status || '—'})} severity={status === 'DONE' ? 'success' : status === 'IN_PROGRESS' ? 'info' : 'warning'}/>}/>
      {machine.capacity.filter(c => c.minutes !== null && c.planned_minutes > c.minutes).map((c, i) => <div className="p-mt-2" key={i}><Message severity="warn" text={t('capacityOverload', {date: c.date, shift: c.shift, planned: number(c.planned_minutes), available: number(c.minutes)})}/></div>)}
      {machine.capacity.some(c => c.missing_standard_time) && <div className="p-mt-2"><Message severity="info" text={t('capacityMissingStandardTime')}/></div>}
      {canAssign && <Button className="p-mt-3" label={t('capacityAssign')} icon="pi pi-plus" onClick={() => open(machine)}/>}
    </section>)}
    <Dialog visible={!!selected} header={`${t('capacityAssign')} • ${selected?.name || ''}`} modal style={{width: '48rem', maxWidth: '95vw'}} onHide={() => { if (!saving) { ++searchSequence.current; setSelected(null); } }}
      footer={<><Button label={t('cancel')} className="p-button-text" disabled={saving} onClick={() => setSelected(null)}/><Button label={t('capacityAssign')} icon="pi pi-check" loading={saving}
        disabled={!form.operation?.id || !form.group || !form.date || !(form.quantity > 0) || !group?.time_from || !group?.time_until} onClick={save}/></>}>
      <div className="p-fluid">
        {dialogError && <Message severity="error" text={t(dialogError, {defaultValue: dialogError})}/>}
        {!selected?.groups.length && <Message severity="warn" text={t('capacityConfigureGroups')}/>}
        <div className="p-field"><label htmlFor="capacity-operation">{t('capacityOperation')}</label><AutoComplete inputId="capacity-operation" value={form.operation} suggestions={suggestions} completeMethod={search} field="label" dropdown forceSelection
          onChange={e => setForm({...form, operation: e.value})} placeholder={t('capacitySearch')}/></div>
        <div className="p-grid">
          <div className="p-field p-col-12 p-md-6"><label htmlFor="capacity-date">{t('capacityDate')}</label><InputText id="capacity-date" type="date" min={moment().format('YYYY-MM-DD')} value={form.date || ''} onChange={e => setForm({...form, date: e.target.value})}/></div>
          <div className="p-field p-col-12 p-md-6"><label htmlFor="capacity-group">{t('capacityShift')}</label><Dropdown inputId="capacity-group" value={form.group} options={selected?.groups || []} optionLabel="name" optionValue="id" onChange={e => setForm({...form, group: e.value})}/>
            {group && <small>{group.time_from && group.time_until ? `${group.time_from} – ${group.time_until}` : t('capacityConfigureTimes')}</small>}</div>
          <div className="p-field p-col-12 p-md-6"><label htmlFor="capacity-quantity">{t('quantity')} {form.operation?.unit || ''}</label><InputNumber inputId="capacity-quantity" value={form.quantity} min={0} maxFractionDigits={3} onValueChange={e => setForm({...form, quantity: e.value})}/>
            {form.operation?.remaining_quantity != null && <small>{t('capacityRemaining', {quantity: number(form.operation.remaining_quantity)})}</small>}</div>
          <div className="p-field p-col-12 p-md-6"><label htmlFor="capacity-minutes">{t('shiftStandardTime')}</label><InputNumber inputId="capacity-minutes" value={form.minutes} min={0} maxFractionDigits={2} onValueChange={e => setForm({...form, minutes: e.value})}/></div>
        </div>
        <div className="p-field"><label htmlFor="capacity-note">{t('note')}</label><InputText id="capacity-note" value={form.note || ''} onChange={e => setForm({...form, note: e.target.value})}/></div>
        <Message severity="info" text={t('capacityAssignmentHelp')}/>
      </div>
    </Dialog>
  </>;
}
